import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  X,
  CheckCircle2,
  AlertTriangle,
  Loader2,
  Users,
  UserCheck,
  UserX,
  FileSpreadsheet,
} from 'lucide-react';
import Modal from './Modal';
import {
  fetchJobStatusRequest,
  fetchJobStatusSuccess,
  clearCurrentJob,
} from '../../store/admin/student-bulk-upload/store/_actions';

// One active job at a time (simplest, per the spec). The jobId lives on the
// server; the browser only persists enough to resume polling after a refresh,
// route change, or tab reopen — which is why this is keyed off localStorage and
// mounted app-wide (in DashboardLayout) rather than inside the upload page.
const STORAGE_KEY = 'student_upload_job';
const POLL_INTERVAL = 4000;

function loadStoredJob() {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY));
  } catch {
    return null;
  }
}
function saveStoredJob(job) {
  if (!job) localStorage.removeItem(STORAGE_KEY);
  else localStorage.setItem(STORAGE_KEY, JSON.stringify(job));
}

export default function StudentUploadJobWatcher() {
  const dispatch = useDispatch();
  const currentJob = useSelector((s) => s.studentBulkUpload?.currentJob);
  const uploading = useSelector((s) => s.studentBulkUpload?.uploading);

  const [showReport, setShowReport] = useState(false);
  const restoredRef = useRef(false);
  const pollRef = useRef(null);

  // Resume an in-flight job after refresh / navigation / tab reopen.
  useEffect(() => {
    if (!currentJob) {
      const saved = loadStoredJob();
      if (saved?.id) {
        dispatch(fetchJobStatusSuccess(saved)); // seed redux from localStorage
        if (saved.status === 'pending' || saved.status === 'processing') {
          dispatch(fetchJobStatusRequest(saved.id));
        }
      }
    }
    restoredRef.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Mirror every job update into localStorage; clear it once the job is gone.
  useEffect(() => {
    if (!restoredRef.current) return;
    if (currentJob?.id) saveStoredJob(currentJob);
    else saveStoredJob(null);
  }, [currentJob]);

  // Poll every 4s while the job is still running.
  useEffect(() => {
    clearInterval(pollRef.current);
    if (currentJob?.id && (currentJob.status === 'pending' || currentJob.status === 'processing')) {
      pollRef.current = setInterval(
        () => dispatch(fetchJobStatusRequest(currentJob.id)),
        POLL_INTERVAL
      );
    }
    return () => clearInterval(pollRef.current);
  }, [currentJob?.id, currentJob?.status, dispatch]);

  const handleDismiss = () => {
    setShowReport(false);
    dispatch(clearCurrentJob());
  };

  if (!uploading && !currentJob) return null;

  const status = uploading ? 'uploading' : currentJob?.status;
  const total = currentJob?.total_records || 0;
  const created = currentJob?.processed_records || 0;
  const existing = currentJob?.already_existing || 0;
  const failed = currentJob?.failed_records || 0;
  const done = created + failed + existing;
  const pct = total > 0 ? Math.round((done / total) * 100) : 0;

  const isActive = status === 'uploading' || status === 'pending' || status === 'processing';
  const isCompleted = status === 'completed';
  const isFailed = status === 'failed';

  const resultJson = Array.isArray(currentJob?.result_json) ? currentJob.result_json : [];
  const successRows = resultJson.filter((r) => r.status === 'success');
  const existingRows = resultJson.filter((r) => r.status === 'already_existing');
  const failedRows = resultJson.filter((r) => r.status === 'failed');

  return (
    <>
      {/* ── Floating progress banner (bottom-right; chatbot lives bottom-left) ── */}
      <div className="fixed bottom-6 right-6 z-[60] w-[340px] max-w-[calc(100vw-3rem)]">
        <div className="bg-white rounded-2xl border border-gray-100 shadow-2xl overflow-hidden">
          {/* Header strip */}
          <div
            className={`px-4 py-3 flex items-center gap-2.5 ${
              isCompleted
                ? 'bg-emerald-50'
                : isFailed
                ? 'bg-red-50'
                : 'bg-blue-50'
            }`}
          >
            <div
              className={`w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0 ${
                isCompleted ? 'bg-emerald-100' : isFailed ? 'bg-red-100' : 'bg-blue-100'
              }`}
            >
              {isActive && <Loader2 className="w-4 h-4 text-blue-600 animate-spin" />}
              {isCompleted && <CheckCircle2 className="w-4 h-4 text-emerald-600" />}
              {isFailed && <AlertTriangle className="w-4 h-4 text-red-600" />}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-bold text-gray-900 leading-tight">
                {status === 'uploading' && 'Queuing upload…'}
                {status === 'pending' && 'Upload queued'}
                {status === 'processing' && 'Uploading students…'}
                {isCompleted && 'Upload complete'}
                {isFailed && 'Upload failed'}
              </p>
              {currentJob?.filename && (
                <p className="text-xs text-gray-400 truncate flex items-center gap-1">
                  <FileSpreadsheet className="w-3 h-3 flex-shrink-0" />
                  {currentJob.filename}
                </p>
              )}
            </div>
            {(isCompleted || isFailed) && (
              <button
                onClick={handleDismiss}
                className="p-1 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-white/60 transition-colors"
                title="Dismiss"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>

          {/* Body */}
          <div className="px-4 py-3.5">
            {isActive && (
              <>
                <div className="flex items-center justify-between mb-1.5">
                  <span className="text-xs font-semibold text-gray-600">
                    {status === 'uploading'
                      ? 'Sending file to server'
                      : `${done} of ${total || '—'} processed`}
                  </span>
                  {status !== 'uploading' && (
                    <span className="text-xs font-bold text-blue-700">{pct}%</span>
                  )}
                </div>
                <div className="h-2 bg-blue-100 rounded-full overflow-hidden">
                  <div
                    className={`h-full bg-blue-500 rounded-full transition-all duration-500 ${
                      status === 'uploading' ? 'animate-pulse' : ''
                    }`}
                    style={{ width: status === 'uploading' ? '40%' : `${pct}%` }}
                  />
                </div>
                <p className="text-xs text-gray-400 mt-2">
                  Safe to navigate away — this keeps running in the background.
                </p>
              </>
            )}

            {isCompleted && (
              <>
                <div className="grid grid-cols-3 gap-2 mb-3">
                  <div className="bg-emerald-50 rounded-xl px-2 py-2 text-center">
                    <p className="text-lg font-black text-emerald-600 leading-none">{created}</p>
                    <p className="text-[11px] text-emerald-700 mt-1">Created</p>
                  </div>
                  <div className="bg-amber-50 rounded-xl px-2 py-2 text-center">
                    <p className="text-lg font-black text-amber-600 leading-none">{existing}</p>
                    <p className="text-[11px] text-amber-700 mt-1">Existing</p>
                  </div>
                  <div className="bg-red-50 rounded-xl px-2 py-2 text-center">
                    <p className="text-lg font-black text-red-600 leading-none">{failed}</p>
                    <p className="text-[11px] text-red-700 mt-1">Failed</p>
                  </div>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => setShowReport(true)}
                    className="flex-1 py-2 bg-blue-600 text-white text-xs font-bold rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    View report
                  </button>
                  <button
                    onClick={handleDismiss}
                    className="px-4 py-2 border border-gray-200 text-gray-600 text-xs font-medium rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Dismiss
                  </button>
                </div>
              </>
            )}

            {isFailed && (
              <>
                <p className="text-sm text-gray-600 mb-3">
                  The upload job could not be completed. Check the file and try again.
                </p>
                <button
                  onClick={handleDismiss}
                  className="w-full py-2 border border-gray-200 text-gray-600 text-xs font-medium rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Dismiss
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* ── Result report modal ── */}
      <Modal
        isOpen={showReport}
        onClose={() => setShowReport(false)}
        title="Bulk Upload Report"
        size="lg"
      >
        <div className="space-y-5">
          {/* Summary */}
          <div className="grid grid-cols-3 gap-3">
            <div className="bg-emerald-50 border border-emerald-100 rounded-xl p-3 text-center">
              <UserCheck className="w-5 h-5 text-emerald-600 mx-auto mb-1" />
              <p className="text-xl font-black text-emerald-600">{created}</p>
              <p className="text-xs text-emerald-700">Created</p>
            </div>
            <div className="bg-amber-50 border border-amber-100 rounded-xl p-3 text-center">
              <Users className="w-5 h-5 text-amber-600 mx-auto mb-1" />
              <p className="text-xl font-black text-amber-600">{existing}</p>
              <p className="text-xs text-amber-700">Already existing</p>
            </div>
            <div className="bg-red-50 border border-red-100 rounded-xl p-3 text-center">
              <UserX className="w-5 h-5 text-red-600 mx-auto mb-1" />
              <p className="text-xl font-black text-red-600">{failed}</p>
              <p className="text-xs text-red-700">Failed</p>
            </div>
          </div>

          {/* Failed rows — most actionable, shown first */}
          {failedRows.length > 0 && (
            <div>
              <h4 className="text-sm font-bold text-red-800 mb-2 flex items-center gap-1.5">
                <AlertTriangle className="w-4 h-4" /> Failed rows ({failedRows.length})
              </h4>
              <p className="text-xs text-gray-500 mb-2">
                Fix these rows in your Excel and re-upload the file — already-created students
                come back as “already existing”, so re-uploading the whole file is safe.
              </p>
              <div className="max-h-48 overflow-y-auto rounded-xl border border-red-100 divide-y divide-red-50">
                {failedRows.map((r, i) => (
                  <div key={i} className="px-3 py-2 flex items-start gap-3 text-sm">
                    <span className="text-xs font-mono font-bold text-red-700 bg-red-50 rounded px-1.5 py-0.5 flex-shrink-0">
                      Row {r.row}
                    </span>
                    <div className="min-w-0">
                      <p className="font-medium text-gray-800 truncate">{r.username || '—'}</p>
                      <p className="text-xs text-red-600">{r.reason || 'Unknown error'}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Already existing */}
          {existingRows.length > 0 && (
            <div>
              <h4 className="text-sm font-bold text-amber-800 mb-2">
                Already existing ({existingRows.length})
              </h4>
              <div className="max-h-40 overflow-y-auto rounded-xl border border-amber-100 divide-y divide-amber-50">
                {existingRows.map((r, i) => (
                  <div key={i} className="px-3 py-2 flex items-center gap-3 text-sm">
                    <span className="text-xs font-mono text-amber-700 flex-shrink-0">Row {r.row}</span>
                    <span className="text-gray-700 truncate">{r.username || '—'}</span>
                    {r.reason && <span className="text-xs text-gray-400 ml-auto">{r.reason}</span>}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Success (collapsed count + list) */}
          {successRows.length > 0 && (
            <div>
              <h4 className="text-sm font-bold text-emerald-800 mb-2">
                Created ({successRows.length})
              </h4>
              <div className="max-h-40 overflow-y-auto rounded-xl border border-emerald-100 divide-y divide-emerald-50">
                {successRows.map((r, i) => (
                  <div key={i} className="px-3 py-2 flex items-center gap-3 text-sm">
                    <span className="text-xs font-mono text-emerald-700 flex-shrink-0">Row {r.row}</span>
                    <span className="text-gray-700 truncate">{r.username || '—'}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {resultJson.length === 0 && (
            <p className="text-sm text-gray-500 text-center py-4">
              No per-row detail available for this job.
            </p>
          )}

          <div className="flex justify-end pt-2">
            <button
              onClick={() => setShowReport(false)}
              className="px-5 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 text-sm font-medium"
            >
              Close
            </button>
          </div>
        </div>
      </Modal>
    </>
  );
}
